import React, { FC, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import zoomSdk from '@zoom/appssdk';

import LoginModal from 'components/modal/basic-modal.component';
import LoginCard from 'components/card/login-card.component';
import {
  VRS_TERMS_LINK,
  VRI_TERMS_LINK,
} from 'utils/constants/termsAndCondition';

const LoginSelectorForm: FC = () => {
  const { vrsSignIn, zoomVriSignIn } = useFlags();
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);

  const handleOpenUrl = () => {
    zoomSdk.openUrl({
      url: `${process.env.REACT_APP_API_BASE_URL}/zoomapp/mailto?to=SICustomerSupport@sorenson.com`,
    });
  };

  return (
    <Box mb={'16px'} id='login-selector-form'>
      <Box mb={'27px'} id='login-selector-form-subtitle'>
        <Typography
          sx={{
            color: 'grey.800',
          }}
          variant='caption'
        >
          Let&apos;s help you get connected to an interpreter. Are you a
          Sorenson VRS user or{' '}
          <Button
            id='sign-up-btn'
            onClick={handleOpenUrl}
            style={{
              textDecoration: 'underline',
              textTransform: 'none',
              color: '#006A62',
              textDecorationColor: '#006A62',
              backgroundColor: 'transparent',
              padding: 0,
              textAlign: 'left',
            }}
          >
            do you need to sign up for Sorenson Express?
          </Button>
        </Typography>
      </Box>
      <Box mb={'9px'} id='login-selector-video'>
        <iframe
          src='https://player.vimeo.com/video/818790786?h=0f16f6c05b'
          style={{
            width: '100%',
            height: '180px',
            backgroundColor: 'black',
            border: 'none',
          }}
          allowFullScreen
          title='Video Relay Service or Video Remote Interpreting'
        />
      </Box>
      <Box mb={'24px'} id='login-selector-asl-with-captions'>
        <Typography sx={{ color: 'grey.700' }}>
          (See explanation in ASL with captions)
        </Typography>
      </Box>
      <Box mb={'12px'} mt={'12px'} id='login-modal'>
        <LoginModal
          btnText='Get an interpreter'
          headline='Let’s help you get connected to an interpreter'
        >
          <Box
            id='login-card'
            display='flex'
            flexDirection='row'
            justifyContent='center'
            flexWrap='wrap'
            boxSizing='border-box'
            style={{ marginTop: 16, gap: '14px' }}
          >
            {vrsSignIn && (
              <Box style={{ marginRight: '0' }} id='login-card-vrs'>
                <LoginCard
                  headlineText='I am using Sorenson VRS'
                  bodyText='Sign in with a Sorenson VP phone number'
                  handleClick={() => navigate('vrs')}
                  termsLink={VRS_TERMS_LINK}
                />
              </Box>
            )}
            {zoomVriSignIn && (
              <Box id='login-card-vri'>
                <LoginCard
                  headlineText='I am using Sorenson Express'
                  bodyText='Sign in with a Sorenson Express account'
                  handleClick={loginWithRedirect}
                  termsLink={VRI_TERMS_LINK}
                />
              </Box>
            )}
          </Box>
          <Box
            id='video-relay-services-vrs-link'
            onClick={() => setIsActive(!isActive)}
            sx={{
              cursor: 'pointer',
              display: 'inline',
              marginBottom: '8px',
            }}
          >
            <Typography
              id='video-relay-services-vrs-link-text'
              variant='caption'
              fontSize={'12px'}
              lineHeight={'18px'}
              sx={{
                textDecoration: 'underline',
                color: 'info.light',
                textDecorationColor: 'info.light',
              }}
            >
              When can I use video relay services (VRS)?
            </Typography>
          </Box>
          {isActive && (
            <Box
              id='video-relay-services-vrs-link-text-block'
              sx={{
                width: 456,
                '@media (max-width: 510px)': {
                  width: '100%',
                },
              }}
            >
              <Typography
                id='video-relay-services-vrs-link-text-explanation'
                fontSize={'12px'}
                variant='caption'
                lineHeight={'18px'}
              >
                Sorenson VRS is only available if you are eligible for VRS and
                have registered in the FCC&apos;s User Registration Database.
                The cost of VRS is paid by a federally administered fund.
                Accordingly, use of Sorenson Engage for VRS calling is subject
                to compliance with FCC regulations and Sorenson&apos;s VRS EULA.
                Under federal law, you may use VRS only if you have a speech or
                hearing disability and need VRS in order to communicate. Also,
                call participants must be in a different location than the other
                individuals on the call. VRS cannot be used for webinars or with
                a privacy screen because the interpreter must be able to see the
                Deaf participant at all times.
              </Typography>
            </Box>
          )}
        </LoginModal>
      </Box>
    </Box>
  );
};

export default LoginSelectorForm;
